import styled, { css, keyframes } from "styled-components";
import { LAYER_COLORS } from "../../../constants/design/colors";
import { BORDER_RADIUS, SPACING } from "../../../constants/design/spacing";

/**
 * Loading placeholder shaped like the content it stands in for (DESIGN.md,
 * Loading States). The shimmer is decorative motion, so it is switched off for
 * `prefers-reduced-motion: reduce`, leaving a static block. Placeholders are
 * hidden from assistive tech; the owning region announces loading instead.
 */
const shimmer = keyframes`
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
`;

type Radius = "sm" | "md" | "lg" | "full";

// One declaration per token, so every radius here is a named BORDER_RADIUS key.
const RADII: Record<Radius, ReturnType<typeof css>> = {
  sm: css`border-radius: ${BORDER_RADIUS.sm}px;`,
  md: css`border-radius: ${BORDER_RADIUS.md}px;`,
  lg: css`border-radius: ${BORDER_RADIUS.lg}px;`,
  full: css`border-radius: ${BORDER_RADIUS.full}px;`,
};

const Block = styled.span<{ $w: string; $h: string; $r: Radius }>`
  display: inline-block;
  vertical-align: middle;
  width: ${({ $w }) => $w};
  height: ${({ $h }) => $h};
  ${({ $r }) => RADII[$r]}
  background: linear-gradient(
    90deg,
    ${LAYER_COLORS.three} 25%,
    ${LAYER_COLORS.four} 50%,
    ${LAYER_COLORS.three} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${LAYER_COLORS.three};
  }
`;

const toCss = (v: number | string) => (typeof v === "number" ? `${v}px` : v);

export interface ISkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: Radius;
  className?: string;
}

export function Skeleton({ width = "100%", height = 12, radius = "sm", className }: ISkeletonProps) {
  return (
    <Block
      className={className}
      $w={toCss(width)}
      $h={toCss(height)}
      $r={radius}
      aria-hidden="true"
      data-testid="skeleton"
    />
  );
}

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.two}px;
`;

const Row = styled.div`
  display: flex;
  gap: ${SPACING.three}px;
`;

/** Table-shaped placeholder: `rows` rows of `columns` cells (DESIGN.md: shimmer rows). */
export function SkeletonRows({
  rows = 3,
  columns = 4,
  label = "Loading",
}: {
  rows?: number;
  columns?: number;
  /** Announced once for the whole block. */
  label?: string;
}) {
  return (
    <Rows role="status" aria-label={label} aria-busy="true">
      {Array.from({ length: rows }, (_, r) => (
        <Row key={r}>
          {Array.from({ length: columns }, (__, c) => (
            <Skeleton key={c} height={14} />
          ))}
        </Row>
      ))}
    </Rows>
  );
}

export default Skeleton;
